import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, Loader2, Radar, Search } from 'lucide-react'
import { Card, CardContent } from '../../components/ui/card'
import { Button } from '../../components/ui/button'
import { supabase } from '../../lib/supabaseClient'

// /search — AI life search. One input box, natural-language retrieval
// across every module ("when did I last see the dentist", "weeks I
// worked out 4+ times"). The life-search edge function plans which
// sources to read, fetches just those, and answers with dates.

const EXAMPLE_KEYS = ['lifeSearch.ex1', 'lifeSearch.ex2', 'lifeSearch.ex3']

export function LifeSearchPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [question, setQuestion] = useState('')
  const [busy, setBusy] = useState(false)
  const [result, setResult] = useState(null) // { answer, sources, from, to } | { error } | { empty }

  const ask = async (q) => {
    const text = (q ?? question).trim()
    if (!text || busy) return
    if (q) setQuestion(q)
    setBusy(true)
    setResult(null)
    const { data, error } = await supabase.functions.invoke('life-search', { body: { question: text } })
    setBusy(false)
    setResult(error || !data ? { error: true } : data)
  }

  return (
    <div className="space-y-4 content-fade pb-8">
      <div className="flex items-center gap-2 mb-2">
        <button
          type="button"
          onClick={() => navigate('/')}
          aria-label={t('settings.back')}
          className="p-2 -ms-2 rounded-md hover:bg-accent text-foreground"
        >
          <ArrowLeft className="w-5 h-5 rtl:-scale-x-100" />
        </button>
        <h1 className="flex-1 min-w-0 text-lg font-bold text-foreground truncate" dir="auto">
          {t('lifeSearch.title')}
        </h1>
      </div>

      <form
        onSubmit={(e) => { e.preventDefault(); ask() }}
        className="flex gap-2"
      >
        <input
          type="text"
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          placeholder={t('lifeSearch.placeholder')}
          maxLength={500}
          className="h-11 flex-1 min-w-0 px-3 rounded-lg border border-input bg-background text-foreground text-sm"
          dir="auto"
        />
        <Button type="submit" disabled={busy || !question.trim()} className="h-11">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
        </Button>
      </form>

      {!result && !busy && (
        <div className="space-y-2">
          <p className="text-xs text-muted-foreground px-1" dir="auto">{t('lifeSearch.hint')}</p>
          {EXAMPLE_KEYS.map((key) => (
            <button
              key={key}
              type="button"
              onClick={() => ask(t(key))}
              className="w-full text-start px-3 py-2.5 rounded-lg border border-border text-sm text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
              dir="auto"
            >
              {t(key)}
            </button>
          ))}
        </div>
      )}

      {busy && (
        <div className="flex flex-col items-center gap-2 py-10 text-muted-foreground">
          <Radar className="w-8 h-8 animate-pulse text-app-primary-500" />
          <p className="text-sm" dir="auto">{t('lifeSearch.searching')}</p>
        </div>
      )}

      {result?.error && (
        <p className="text-sm text-red-500 px-1" dir="auto">{t('lifeSearch.failed')}</p>
      )}
      {result?.empty && (
        <p className="text-sm text-muted-foreground px-1" dir="auto">{t('lifeSearch.noData')}</p>
      )}
      {result?.answer && (
        <Card>
          <CardContent className="p-4 space-y-3">
            <p className="text-sm text-foreground whitespace-pre-wrap leading-relaxed" dir="auto">
              {result.answer}
            </p>
            <div className="flex flex-wrap items-center gap-1.5 pt-1 border-t border-border/60">
              <span className="text-[10px] text-muted-foreground" dir="auto">{t('lifeSearch.sourcesLabel')}</span>
              {(result.sources || []).map((s) => (
                <span
                  key={s}
                  className="px-1.5 py-0.5 rounded bg-app-primary-500/10 text-app-primary-500 text-[10px] font-medium"
                  dir="ltr"
                >
                  {s}
                </span>
              ))}
              {result.from && (
                <span className="text-[10px] text-muted-foreground ms-auto" dir="ltr">
                  {result.from} → {result.to}
                </span>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}

export default LifeSearchPage
